import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal, KdsTextArea } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { FormProvider, useForm } from "react-hook-form";

import { useToasts } from "@/hooks";
import { useOrderItemsPreMedia } from "@/oms/hooks/queries/useOrderItemPreMedia";
import {
  type ModalPreMediaRequestDetailsFormSchema,
  modalPreMediaRequestDetailsFormSchema,
} from "./ModalPreMediaRequestDetailsForm.schema";
import { type ModalPreMediaRequestDetailsFormProps } from "./ModalPreMediaRequestDetailsForm.types";

export const ModalPreMediaRequestDetailsForm = ({
  isOpen,
  onCloseModal,
  orderItem,
}: ModalPreMediaRequestDetailsFormProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const methods = useForm<ModalPreMediaRequestDetailsFormSchema>({
    resolver: zodResolver(modalPreMediaRequestDetailsFormSchema),
  });
  const { useRequestPreMediaRequestDetails } = useOrderItemsPreMedia();
  const {
    mutateAsync: sendRequestForPreMediaRequestDetails,
    isPending: isSubmittingRequestForPreMediaRequestDetails,
  } = useRequestPreMediaRequestDetails();

  const handleSubmitRequestForPreMediaRequestDetails = async () => {
    try {
      const res = await sendRequestForPreMediaRequestDetails({
        orderId: orderItem.orderId,
        orderItemId: orderItem.id,
        command: {
          isValidated: false,
          comment: methods.getValues("comment"),
        },
      });

      if (res.status === 201 || res.status === 200) {
        add({
          state: "success",
          title: t("toast.type.success"),
        });
        onCloseModal();
        methods.reset();
      } else {
        add({
          state: "error",
          title: t("toast.generic.error"),
        });
      }
    } catch {
      add({
        state: "error",
        title: t("toast.generic.error"),
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <KdsModal
        isOpen={isOpen}
        callbackOnClose={onCloseModal}
        size="md"
        dialogProps={{
          dialogHeaderProps: {
            title: t(
              "order.myOrdersItemDetails.preMedia.requestForPreMediaRequestDetails"
            ),
            description: t(
              "order.myOrdersItemDetails.preMedia.requestDetailsDescrption"
            ),
          },
          content: (
            <KdsTextArea
              label={t("inputs.comment.label")}
              placeholder={t("order.myOrdersItemDetails.preMedia.comment")}
              maxLength={4000}
              hasError={Boolean(methods.formState.errors.comment)}
              {...methods.register("comment", { required: true })}
            />
          ),
          dialogFooterProps: {
            buttonProps: [
              {
                variant: "ghost",
                color: "neutral",
                children: t("buttons.cancel"),
                onPress: onCloseModal,
              },
              {
                children: t("order.myOrdersItemDetails.preMedia.send"),
                isLoading: isSubmittingRequestForPreMediaRequestDetails,
                disabled: isSubmittingRequestForPreMediaRequestDetails,
                type: "submit",
                dataCy: "submitRequestForPreMediaRequestDetailsButton",
                onPress: () =>
                  void methods.handleSubmit(
                    handleSubmitRequestForPreMediaRequestDetails
                  )(),
              },
            ],
          },
        }}
      />
    </FormProvider>
  );
};
